import React, {
  useCallback,
  useEffect,
  useState,
  type ChangeEvent,
  type FormEvent,
} from "react";
import {
  Box,
  Typography,
  Button,
  Grid,
  CircularProgress,
  InputAdornment,
  Autocomplete,
  type SelectChangeEvent,
  Card,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  useTheme,
} from "@mui/material";
import { AddCircleOutlineOutlined, Check } from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { tokens } from "../../../theme";
import type { RootState } from "../../../store/Index";
import apiService from "../../../service/Api";
import {
  showErrorToast,
  showSuccessToast,
} from "../../../components/common/ShowToast";
import CustomTextField from "../../../components/ui/Textfield";
import PhoneNumberField from "../../../components/ui/PhoneNumberField";
import PhotoEditor from "../../../components/PhotoEditor";
import { CONTACT_COUNTRIES } from "../../../utils/countries";

interface ICompany {
  id: number;
  name: string;
  // API field names vary between environments; support both.
  email?: string;
  phone?: string;
  primary_email?: string;
  phone_number?: string;
  address_one?: string;
  address_two?: string;
  suburb?: string;
  city?: string;
  country?: string;
  website?: string;
  facebook_profile?: string;
  instagram_profile?: string;
  twitter_profile?: string;
  linkedin_profile?: string;
}

interface ICompanyBranch {
  id: number;
  name: string;
  company_id?: number;
  street_address?: string;

}

interface IContactData {
  contact_type: "company";
  name: string;
  email: string;
  phone: string;
  address_one: string;
  address_two: string;
  suburb: string;
  city: string;
  country: string;
  website: string;
  notes: string;
  facebook_profile: string;
  instagram_profile: string;
  twitter_profile: string;
  linkedin_profile: string;
  company_id: number | null;
  company_branch: number | null;
  contact_image: string | null;
  contactImageFile: File | null;
}

interface IContactResponse {
  id: number;
  [key: string]: unknown;
}

interface IAddCompanyContactProps {
  onContactAdded?: (contact: IContactData & { id: number }) => void;
  openModal?: boolean;
  handleClose?: () => void;
}

const getErrorMessage = (err: unknown, fallback: string) => {
  const responseData = (err as { response?: { data?: Record<string, unknown> } })
    ?.response?.data;
  if (!responseData || typeof responseData !== "object") {
    return fallback;
  }
  const typedData = responseData as {
    detail?: string;
    message?: string;
    error?: string;
  };
  return (
    typedData.detail ||
    typedData.message ||
    typedData.error ||
    Object.values(responseData).flat().join(" ") ||
    fallback
  );
};

const AddCompanyContact: React.FC<IAddCompanyContactProps> = ({
  onContactAdded,
  openModal = false,
  handleClose,
}) => {
  const navigate = useNavigate();
  const reduxTheme = useSelector((state: RootState) => state.theme);
  const theme = useTheme();
  const colors = tokens(theme.palette.mode, reduxTheme);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [, setServerResponse] = useState<{
    success: boolean;
    message: string;
  } | null>(null);
  const [emailError, setEmailError] = useState("");
  const [companyLoading, setCompanyLoading] = useState(false);
  const [branchLoading, setBranchLoading] = useState(false);
  const [companyOptions, setCompanyOptions] = useState<ICompany[]>([]);
  const [branchOptions, setBranchOptions] = useState<ICompanyBranch[]>([]); // Added state for branchOptions
  const [companySearchTerm, setCompanySearchTerm] = useState("");
  const [openAddCompanyDialog, setOpenAddCompanyDialog] = useState(false);
  const [openAddBranchRepDialog, setOpenAddBranchRepDialog] = useState(false);
  const [openAddBranchDialog, setOpenAddBranchDialog] = useState(false);

  const [branchRepData, setBranchRepData] = useState<{
    name: string;
    email: string;
    phone: string;
  }>({
    name: "",
    email: "",
    phone: "",
  });

  const [newBranchRepData, setNewBranchRepData] = useState<Partial<IContactData>>({
    name: "",
    email: "",
    phone: "",
  });

  const [savingNewBranchRep, setSavingNewBranchRep] = useState(false);
  const [newBranchData, setNewBranchData] = useState({
    name: "",
    address_one: "",
    address_two: "",
  });
  const [savingNewBranch, setSavingNewBranch] = useState(false);

  const [newCompanyData, setNewCompanyData] = useState<Partial<ICompany>>({
    name: "",
    email: "",
    phone: "",
  });
  const [savingNewCompany, setSavingNewCompany] = useState(false);

  const [form, setForm] = useState<IContactData>({
    contact_type: "company",
    name: "",
    email: "",
    phone: "",
    address_one: "",
    address_two: "",
    suburb: "",
    city: "",
    country: "",
    website: "",
    notes: "",
    facebook_profile: "",
    instagram_profile: "",
    twitter_profile: "",
    linkedin_profile: "",
    company_id: null,
    company_branch: null,
    contact_image: null,
    contactImageFile: null,
  });

  const validateEmail = (email: string): string => {
    if (!email) return "";
    const regex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
    if (!regex.test(email)) return "Invalid email format.";
    if (email.startsWith(".")) return "Email cannot start with a dot.";
    if (email.endsWith(".")) return "Email cannot end with a dot.";
    if (email.split("@")[0].endsWith("."))
      return "Local part cannot end with a dot.";
    return "";
  };

  useEffect(() => {
    const timer = setTimeout(() => {
      if (companySearchTerm.length >= 3) {
        fetchCompanies(companySearchTerm);
      } else {
        setCompanyOptions([]);
      }
    }, 500);

    return () => clearTimeout(timer);
  }, [companySearchTerm]);

  useEffect(() => {
    return () => {
      if (form.contact_image && form.contact_image.startsWith("blob:")) {
        URL.revokeObjectURL(form.contact_image);
      }
    };
  }, [form.contact_image]);

  const fetchCompanies = async (search: string) => {
    setCompanyLoading(true);
    try {
      const companyResponse = await apiService.get<{ results: ICompany[] }>(
        `/api/companies/company/?search=${search}`
      );
      setCompanyOptions(companyResponse.data.results || []);
    } catch (err: unknown) {
      console.error("Failed to fetch companies:", err);
      showErrorToast("Failed to load companies. Please try again.");
    } finally {
      setCompanyLoading(false);
    }
  };

  const fetchCompanyDetails = useCallback(async (companyId: number) => {
    try {
      const companyResponse = await apiService.get<ICompany>(
        `/api/companies/company/${companyId}/`
      );
      const company = companyResponse.data;
      setForm((prev) => ({
        ...prev,
        name: company.name || prev.name,
        email: company.email || company.primary_email || prev.email,
        phone: company.phone || company.phone_number || prev.phone,
        address_one: company.address_one || prev.address_one,
        address_two: company.address_two || prev.address_two,
        suburb: company.suburb || prev.suburb,
        city: company.city || prev.city,
        country: company.country || prev.country,
        website: company.website || prev.website,
        facebook_profile: company.facebook_profile || prev.facebook_profile,
        instagram_profile: company.instagram_profile || prev.instagram_profile,
        twitter_profile: company.twitter_profile || prev.twitter_profile,
        linkedin_profile: company.linkedin_profile || prev.linkedin_profile,
      }));
      const resolvedEmail = company.email || company.primary_email || "";
      setEmailError(validateEmail(resolvedEmail));
    } catch (err: unknown) {
      console.error("Failed to fetch company details:", err);
      showErrorToast(
        getErrorMessage(err, "Failed to load company details. Please try again.")
      );
    }
  }, []);

  useEffect(() => {
    if (form.company_id) {
      fetchCompanyDetails(form.company_id);
      fetchBranches(form.company_id);
    } else {
      setBranchOptions([]);
      setForm((prev) => ({ ...prev, company_branch: null }));
    }
  }, [fetchCompanyDetails, form.company_id]);

  const fetchBranches = async (companyId: number) => {
    setBranchLoading(true);
    try {
      const branchResponse = await apiService.get<{
        results: ICompanyBranch[];
      }>(`/api/companies/company-branch/?company_id=${companyId}`);
      setBranchOptions(branchResponse.data.results || []);
    } catch (err: unknown) {
      console.error("Failed to fetch branches:", err);
      showErrorToast(
        getErrorMessage(err, "Failed to load branches. Please try again.")
      );
    } finally {
      setBranchLoading(false);
    }
  };

  const handleChange = (
    e:
      | ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
      | SelectChangeEvent<string>
  ) => {
    const { name, value } = e.target as HTMLInputElement;
    if (name === "email") {
      const error = validateEmail(value);
      setEmailError(error);
      setForm((prev) => ({ ...prev, email: value }));
    } else {
      setForm((prev) => ({ ...prev, [name as keyof IContactData]: value }));
    }
  };

  const handlePhoneInputChange = (value: string) => {
    setForm((prev) => ({ ...prev, phone: value }));
  };

  const handleCompanyChange = (
    event: React.SyntheticEvent,
    value: ICompany | null
  ) => {
    const resolvedEmail = value?.email || value?.primary_email || "";
    const resolvedPhone = value?.phone || value?.phone_number || "";
    setForm((prev) => ({
      ...prev,
      company_id: value?.id || null,
      company_branch: null,
      name: value?.name || "",
      email: resolvedEmail,
      phone: resolvedPhone,
    }));
    setEmailError(validateEmail(resolvedEmail));
  };

  const handleBranchChange = (
    event: React.SyntheticEvent,
    value: ICompanyBranch | null
  ) => {
    setForm((prev) => ({
      ...prev,
      company_branch: value?.id || null,
    }));
  };

  const handleOpenAddCompanyDialog = () => {
    setOpenAddCompanyDialog(true);
    setNewCompanyData({ name: "", email: "", phone: "" });
  };

 const handleOpenAddBranchRepDialog = () => {
    setOpenAddBranchRepDialog(true);
    setNewBranchRepData({ name: "", email: "", phone: "" });
  }

  const handleNewBranchRepChange = (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = event.target;
    setNewBranchRepData((prev) => ({ ...prev, [name]: value }));
  }

  const handlePrimaryBranchRepChange = (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = event.target;
    setBranchRepData((prev) => ({ ...prev, [name]: value }));
  };

  const handlePrimaryBranchRepPhoneChange = (value: string) => {
    setBranchRepData((prev) => ({ ...prev, phone: value }));
  };

  const handleOpenAddBranchDialog = () => {
    setOpenAddBranchDialog(true);
    setNewBranchData({ name: "", address_one: "", address_two: "" });
  };
  const handleNewBranchChange = (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = event.target;
    setNewBranchData((prev) => ({ ...prev, [name]: value }));
  };
  
   const handleCloseAddBranchRepDialog = () => {
    setOpenAddBranchRepDialog(false);
  }
  
  const handleCloseAddBranchDialog = () => {
    setOpenAddBranchDialog(false);
  }

  const handleCloseAddCompanyDialog = () => {
    setOpenAddCompanyDialog(false);
  };

  const handleSaveNewBranchRep = async () => {
    setSavingNewBranchRep(true);
    try {
      if (!form.company_branch) {
        showErrorToast("Please select a company branch first.");
        return;
      }
      if (!newBranchRepData.name) {
        showErrorToast("Branch rep name is required.");
        return;
      }
      if (!newBranchRepData.email && !newBranchRepData.phone) {
        showErrorToast(
          "Provide at least one contact method (email or phone number)."
        );
        return;
      }

      const formData = new FormData();
      formData.append("contact_type", "company");
      formData.append("name", String(newBranchRepData.name || ""));
      if (newBranchRepData.email) formData.append("email", String(newBranchRepData.email));
      if (newBranchRepData.phone) formData.append("phone", String(newBranchRepData.phone));

      // Support both API shapes: prefer company_branch_id, fallback to company_branch.
      let saved = false;
      try {
        formData.append("company_branch_id", String(form.company_branch));
        await apiService.post<IContactResponse>("/api/contacts/contact/", formData, false);
        saved = true;
      } catch (err: unknown) {
        void err;
        // Retry with company_branch if the backend doesn't support company_branch_id
        if (formData.has("company_branch_id")) formData.delete("company_branch_id");
        formData.append("company_branch", String(form.company_branch));
        await apiService.post<IContactResponse>("/api/contacts/contact/", formData, false);
        saved = true;
      }

      if (saved) {
        showSuccessToast("Branch rep added successfully!");
        handleCloseAddBranchRepDialog();
      }
    } catch (error: unknown) {
      console.error("Failed to save new branch rep:", error);
      showErrorToast(
        getErrorMessage(error, "Failed to add branch rep. Please try again.")
      );
    } finally {
      setSavingNewBranchRep(false);
    }
  };

  const handleSaveNewBranch = async () => {
    setSavingNewBranch(true);
    try {
      if (!form.company_id) {
        showErrorToast("Please select a company first.");
        return;
      }
      if (!newBranchData.name) {
        showErrorToast("Branch name is required.");
        return;
      }

      const streetAddress = [newBranchData.address_one, newBranchData.address_two]
        .map((s) => (s || "").trim())
        .filter(Boolean)
        .join(", ");

      let response;
      try {
        response = await apiService.post<ICompanyBranch>(
          "/api/companies/company-branch/",
          {
            company_id: form.company_id,
            name: newBranchData.name,
            street_address: streetAddress || undefined,
          }
        );
      } catch (err: unknown) {
        void err;
        // Fallback for older API shapes that used `location`
        const fallbackPayload = {
          company_id: form.company_id,
          name: newBranchData.name,
          location: streetAddress || undefined,
        };
        response = await apiService.post<ICompanyBranch>(
          "/api/companies/company-branch/",
          fallbackPayload
        );
      }

      const savedBranch = response.data;
      setBranchOptions((prev) => [...prev, savedBranch]);
      setForm((prev) => ({
        ...prev,
        company_branch: savedBranch.id,
      }));
      showSuccessToast("Branch added successfully!");
      handleCloseAddBranchDialog();
    } catch (error: unknown) {
      console.error("Failed to save new branch:", error);
      showErrorToast(getErrorMessage(error, "Failed to add branch. Please try again."));
    } finally {
      setSavingNewBranch(false);
    }
  };

  const handlePhoneBranchRepInputChange = (value: string) => {
    setNewBranchRepData((prev) => ({ ...prev, phone: value }));
  };

  const handleNewCompanyChange = (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = event.target;
    setNewCompanyData((prev) => ({ ...prev, [name]: value }));
  };

  const handlePhoneCompanyInputChange = (value: string) => {
    setNewCompanyData((prev) => ({ ...prev, phone: value }));
  };

  const handleSaveNewCompany = async () => {
    setSavingNewCompany(true);
    try {
      const payloadV2 = {
        name: newCompanyData.name,
        primary_email: newCompanyData.email,
        phone_number: newCompanyData.phone,
      };

      // Support both API shapes (primary_email/phone_number vs email/phone).
      let res;
      try {
        res = await apiService.post<ICompany>("/api/companies/company/", payloadV2);
      } catch (err: unknown) {
        void err;
        res = await apiService.post<ICompany>("/api/companies/company/", newCompanyData);
      }
      const savedCompany = res.data;
      showSuccessToast("Company added successfully!");
      setCompanyOptions((prev) => [...prev, savedCompany]);
      const resolvedEmail =
        savedCompany.email || savedCompany.primary_email || newCompanyData.email || "";
      const resolvedPhone =
        savedCompany.phone || savedCompany.phone_number || newCompanyData.phone || "";
      setForm((prev) => ({
        ...prev,
        company_id: savedCompany.id,
        company_branch: null,
        name: savedCompany.name || prev.name,
        email: resolvedEmail,
        phone: resolvedPhone,
      }));
      setEmailError(validateEmail(resolvedEmail));
      handleCloseAddCompanyDialog();
    } catch (error: unknown) {
      console.error("Failed to save new company:", error);
      showErrorToast(getErrorMessage(error, "Failed to add company. Please try again."));
    } finally {
      setSavingNewCompany(false);
    }
  };

  const handleSaveContactImage = async (
    imageData: string,
    contactId?: number
  ) => {
    try {
      const response = await fetch(imageData);
      const blob = await response.blob();
      const file = new File([blob], `contact_image_${contactId || "new"}.jpg`, {
        type: blob.type,
      });
      setForm((prev) => ({
        ...prev,
        contact_image: imageData,
        contactImageFile: file,
      }));
      showSuccessToast("Contact image saved successfully!");
    } catch (error: unknown) {
      console.error("Failed to process contact image:", error);
      showErrorToast("Failed to process contact image. Please try again.");
    }
  };

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setIsSubmitting(true);
    setServerResponse(null);

    if (!form.name) {
      setServerResponse({
        success: false,
        message: "Company name is required.",
      });
      showErrorToast("Company name is required.");
      setIsSubmitting(false);
      return;
    }

    if (!form.email && !form.phone) {
      setServerResponse({
        success: false,
        message: "Provide at least one contact method (email or phone number).",
      });
      showErrorToast(
        "Provide at least one contact method (email or phone number)."
      );
      setIsSubmitting(false);
      return;
    }

    if (emailError) {
      setServerResponse({
        success: false,
        message: "Please correct the email address error.",
      });
      showErrorToast("Please correct the email address error.");
      setIsSubmitting(false);
      return;
    }

    const formData = new FormData();
    formData.append("contact_type", form.contact_type);
    Object.entries(form).forEach(([key, value]) => {
      if (key === "contactImageFile" && value instanceof File) {
        formData.append("contact_image", value);
      } else if (key !== "contact_image" && value !== null && value !== "") {
        formData.append(key, value.toString());
      }
    });

    try {
      const response = await apiService.post<IContactResponse>(
        "/api/contacts/contact/",
        formData,
        false
      );
      setServerResponse({
        success: true,
        message: "Company contact added successfully.",
      });
      showSuccessToast("Company contact added successfully.");

      if (onContactAdded) {
        onContactAdded({ ...form, id: response.data.id });
      }

      if (openModal && handleClose) {
        handleClose();
      } else {
        setTimeout(() => {
          navigate(`/contacts/company/${response.data.id}`);
        }, 2000);
      }
    } catch (err: unknown) {
      console.error("Failed to add company contact:", err);
      const errorMessage = getErrorMessage(
        err,
        "Failed to add company contact. Please try again."
      );
      setServerResponse({
        success: false,
        message: errorMessage,
      });
      showErrorToast(errorMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  const isEmailValid = !emailError && form.email.includes("@");

  const textFieldStyles = {
    "& .MuiOutlinedInput-root": {
      borderRadius: "10px",
      "& fieldset": { borderColor: colors.primary[300] },
      "&:hover fieldset": {
        borderColor: colors.primary[300],
      },
      "&.Mui-focused fieldset": {
        borderColor: colors.primary[400],
      },
    },
    "& .MuiInputLabel-root": {
      color: colors.primary[300],
    },
    "& .MuiInputBase-input": {
      color: colors.primary[700],
    },
  };

  return (
    <Card
      sx={{
        maxWidth: 1200,
        mx: "auto",
        p: 4,
        borderRadius: 3,
        boxShadow: "0 1px 3px rgba(0,0,0,0.06)",
        background: colors.neutral[900],
        borderTop: `5px solid ${colors.secondary[50]}`,
        borderTopWidth: 5,
      }}
    >
      <Typography
        variant="h4"
        sx={{
          color: colors.primary[400],
          fontWeight: 600,
          mb: 3,
        }}
      >
        Add Company Contact
      </Typography>

      <form onSubmit={handleSubmit}>
        <Box mb={4}>
          <Typography
            variant="h5"
            sx={{
              color: colors.primary[400],
              fontWeight: 600,
              mb: 3,
            }}
          >
            Contact Details
          </Typography>
          <Grid container spacing={3}>
            <Grid size={{ xs: 12, md: 4 }}>
              <Autocomplete
                fullWidth
                options={companyOptions}
                getOptionLabel={(option: ICompany) => option.name || ""}
                value={
                  companyOptions.find((c) => c.id === form.company_id) || null
                }
                onChange={handleCompanyChange}
                onInputChange={(event, newInputValue) => {
                  setCompanySearchTerm(newInputValue);
                }}
                loading={companyLoading}
                renderInput={(params) => (
                  <CustomTextField
                    {...params}
                    label="Company"
                    variant="outlined"
                    InputLabelProps={{ shrink: true }}
                    placeholder="Search the company the Company is from"
                    helperText="Type at least 3 characters to search companies"
                    InputProps={{
                      ...params.InputProps,
                      endAdornment: (
                        <>
                          {companyLoading && (
                            <CircularProgress
                              size={20}
                              sx={{
                                color:
                                  colors.primary[400],
                              }}
                            />
                          )}
                          {params.InputProps.endAdornment}
                        </>
                      ),
                    }}
                    sx={textFieldStyles}
                  />
                )}
              />
              <Button
                startIcon={<AddCircleOutlineOutlined />}
                onClick={handleOpenAddCompanyDialog}
                sx={{
                  mt: 1,
                  color: colors.primary[400],
                  "&:hover": {
                    backgroundColor:
                      colors.neutral[800],
                  },
                }}
              >
                Add Company
              </Button>
            </Grid>
            <Grid size={{ xs: 12, md: 4 }}>
              <CustomTextField
                fullWidth
                label="Company Email"
                name="email"
                value={form.email}
                onChange={handleChange}
                error={!!emailError}
                helperText={emailError}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      {isEmailValid && (
                        <Check
                          sx={{
                            color:
                              colors.primary[400],
                          }}
                        />
                      )}
                    </InputAdornment>
                  ),
                }}
                sx={{
                  ...textFieldStyles,
                  "& .MuiFormHelperText-root": {
                    color: colors.neutral[100],
                  },
                }}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 4 }}>
              <PhoneNumberField
                fullWidth
                label="Company Phone Number"
                value={form.phone}
                onChange={handlePhoneInputChange}
                defaultCountry="ZW"
                variant="outlined"
                sx={{ marginBottom: "15px" }}
              />
            </Grid>
           
            <Grid size={{ xs: 12, md: 4 }}>
              <Autocomplete
                fullWidth
                options={branchOptions}
                getOptionLabel={(option: ICompanyBranch) => option.name || ""}
                value={
                  branchOptions.find(
                    (b: ICompanyBranch) => b.id === form.company_branch
                  ) || null
                }
                onChange={handleBranchChange}
                disabled={!form.company_id || branchLoading}
                loading={branchLoading}
                renderInput={(params) => (
                  <CustomTextField
                    {...params}
                    label="Company Branch"
                    variant="outlined"
                    helperText={
                      form.company_id
                        ? "Select a branch"
                        : "Select a company first"
                    }
                    InputProps={{
                      ...params.InputProps,
                      endAdornment: (
                        <>
                          {branchLoading && (
                            <CircularProgress
                              size={20}
                              sx={{
                                color:
                                  colors.primary[400],
                              }}
                            />
                          )}
                          {params.InputProps.endAdornment}
                        </>
                      ),
                    }}
                    sx={textFieldStyles}
                  />
                )}
              />
              <Button
                startIcon={<AddCircleOutlineOutlined />}
                onClick={handleOpenAddBranchDialog}
                sx={{
                  mt: 1,
                  color: colors.primary[400],
                  "&:hover": {
                    backgroundColor:
                      colors.neutral[800],
                  },
                }}
              >
                Add Branch
              </Button>
            </Grid>

            

            <Grid size={{ xs: 12, md: 4 }}>
              <PhotoEditor
                initialImage={form.contact_image}
                title="Company Profile Photo"
                onSave={(imageData: string) =>
                  handleSaveContactImage(imageData)
                }
              />
            </Grid>
          </Grid>
        </Box>

        {/* <Divider sx={{ my: 4 }} /> */}

        {/* <Box mb={4}>
          <Typography
            variant="h5"
            sx={{
              color: colors.primary[400],
              fontWeight: 600,
              mb: 3,
            }}
          >
            Address
          </Typography>
          <Grid container spacing={2}>
            <Grid size={{ xs: 12, md: 4 }}>
              <CustomTextField
                fullWidth
                label="Address Line 1"
                name="address_one"
                value={form.address_one}
                onChange={handleChange}
                sx={textFieldStyles}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 4 }}>
              <CustomTextField
                fullWidth
                label="Address Line 2"
                name="address_two"
                value={form.address_two}
                onChange={handleChange}
                sx={textFieldStyles}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 4 }}>
              <CustomTextField
                fullWidth
                label="Suburb"
                name="suburb"
                value={form.suburb}
                onChange={handleChange}
                sx={textFieldStyles}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 4 }}>
              <CustomTextField
                fullWidth
                label="City"
                name="city"
                value={form.city}
                onChange={handleChange}
                sx={textFieldStyles}
                select
              >
                <MenuItem value="">
                  <em>None</em>
                </MenuItem>
                {reduxTheme.cities.map((city) => (
                  <MenuItem key={city} value={city}>
                    {city}
                  </MenuItem>
                ))}
              </CustomTextField>
            </Grid>

            <Grid size={{ xs: 12, md: 4 }}>
              <CustomTextField
                fullWidth
                label="Country"
                name="country"
                value={form.country}
                onChange={handleChange}
                select
                sx={textFieldStyles}
              >
                <MenuItem value="" disabled sx={{ color: "#aaa" }}>
                  Select Country
                </MenuItem>
                {CONTACT_COUNTRIES.map(({ code, name }) => (
                  <MenuItem key={code} value={code}>
                    {name}
                  </MenuItem>
                ))}
              </CustomTextField>
            </Grid>
          </Grid>
        </Box> */}

        <Divider sx={{ my: 4 }} />
            {/* Branch Rep Details Section */}
        <Box mb={4}>
          <Typography
            variant="h5"
            sx={{
              color: colors.primary[400],
              fontWeight: 600,
              mb: 3,
            }}
          >
            Branch Rep Details
          </Typography>
          <Grid container spacing={3}>
            <Grid size={{ xs: 12, md: 4 }}>
              <CustomTextField
                fullWidth
                label="Branch Rep Name"
                name="name"
                value={branchRepData.name}
                onChange={handlePrimaryBranchRepChange}
                sx={textFieldStyles}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 4 }}>
              <CustomTextField
                fullWidth
                label="Branch Rep Email"
                name="email"
                value={branchRepData.email}
                onChange={handlePrimaryBranchRepChange}
                sx={{
                  ...textFieldStyles,
                  "& .MuiFormHelperText-root": {
                    color: colors.neutral[100],
                  },
                }}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 4 }}>
              <PhoneNumberField
                fullWidth
                label="Branch Rep Phone Number"
                value={branchRepData.phone}
                onChange={handlePrimaryBranchRepPhoneChange}
                defaultCountry="ZW"
                variant="outlined"
                sx={{ marginBottom: "15px" }}
              />
            </Grid>
           
            

             {/* Branch Rep Social Media Section */}
        <Box mb={4}>
          <Typography
            variant="h5"
            sx={{
              color: colors.primary[400],
              fontWeight: 600,
              mb: 3,
            }}
          >
            Social Media
          </Typography>
          <Grid container spacing={2}>
            <Grid size={{ xs: 12, md: 4 }}>
              <CustomTextField
                fullWidth
                label="Company Website"
                name="website"
                placeholder="https://"
                value={form.website}
                onChange={handleChange}
                sx={textFieldStyles}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 4 }}>
              <CustomTextField
                fullWidth
                label="Company Facebook Profile"
                name="facebook_profile"
                value={form.facebook_profile}
                placeholder="https://"
                onChange={handleChange}
                sx={textFieldStyles}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 4 }}>
              <CustomTextField
                fullWidth
                label="Company LinkedIn Profile"
                name="linkedin_profile"
                value={form.linkedin_profile}
                placeholder="https://"
                onChange={handleChange}
                sx={textFieldStyles}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 4 }}>
              <CustomTextField
                fullWidth
                label="Company X Profile"
                name="twitter_profile"
                value={form.twitter_profile}
                placeholder="https://"
                onChange={handleChange}
                sx={textFieldStyles}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 4 }}>
              <CustomTextField
                fullWidth
                label="Branch Instagram Profile"
                name="instagram_profile"
                value={form.instagram_profile}
                placeholder="https://"
                onChange={handleChange}
                sx={textFieldStyles}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
          </Grid>
        </Box>
        
          </Grid>
          <Button
                startIcon={<AddCircleOutlineOutlined />}
                onClick={handleOpenAddBranchRepDialog}
                sx={{
                  mt: 1,
                  color: colors.primary[400],
                  "&:hover": {
                    backgroundColor:
                      colors.neutral[800],
                  },
                }}
              >
                Add Another Branch Rep
              </Button>
        </Box>

        <Divider sx={{ my: 4 }} />

        <Box mb={4}>
          <Typography
            variant="h5"
            sx={{
              color: colors.primary[400],
              fontWeight: 600,
              mb: 3,
            }}
          >
            Additional Info
          </Typography>
          <Grid container spacing={3}>
            <Grid size={{ xs: 12 }}>
              <CustomTextField
                fullWidth
                label="Notes"
                name="notes"
                multiline
                rows={4}
                value={form.notes}
                onChange={handleChange}
                sx={textFieldStyles}
              />
            </Grid>
          </Grid>
        </Box>

        <Box
          sx={{ display: "flex", justifyContent: "flex-start", gap: 2, mt: 4 }}
        >
          {handleClose && (
            <Button
              variant="outlined"
              onClick={handleClose}
              disabled={isSubmitting}
              sx={{
                borderColor: colors.primary[400],
                color: colors.primary[400],
                "&:hover": {
                  borderColor: colors.primary[300],
                  backgroundColor: colors.primary[50] || colors.primary[800],
                },
              }}
            >
              Cancel
            </Button>
          )}
          <Button
            type="submit"
            variant="contained"
            disabled={isSubmitting}
            sx={{
              backgroundColor: colors.primary[400],
              "&:hover": {
                backgroundColor: colors.primary[300],
              },
            }}
          >
            {isSubmitting ? (
              <CircularProgress
                size={24}
                sx={{ color: colors.neutral[100] }}
              />
            ) : (
              "Save Contact"
            )}
          </Button>
        </Box>
      </form>

      {/* Open Add Company Dialog */}
      <Dialog open={openAddCompanyDialog} onClose={handleCloseAddCompanyDialog}>
        <DialogTitle>Add New Company</DialogTitle>
        <DialogContent>
          <CustomTextField
            autoFocus
            margin="dense"
            name="name"
            label="Company Name"
            type="text"
            fullWidth
            variant="outlined"
            value={newCompanyData.name}
            onChange={handleNewCompanyChange}
            sx={{ mb: 2 }}
          />
          <CustomTextField
            margin="dense"
            name="email"
            label="Email Address"
            type="email"
            fullWidth
            variant="outlined"
            value={newCompanyData.email}
            onChange={handleNewCompanyChange}
            sx={{ mb: 2 }}
          />
          <PhoneNumberField
            margin="dense"
            name="phone"
            label="Phone Number"
            fullWidth
            variant="outlined"
            value={newCompanyData.phone || ""}
            defaultCountry="ZW"
            onChange={handlePhoneCompanyInputChange}
            sx={{ mb: 2 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseAddCompanyDialog} color="secondary">
            Cancel
          </Button>
          <Button
            onClick={handleSaveNewCompany}
            color="primary"
            disabled={savingNewCompany}
          >
            {savingNewCompany ? <CircularProgress size={24} /> : "Save Company"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Open Add Branch Dialog */}
       <Dialog open={openAddBranchDialog} onClose={handleCloseAddBranchDialog}>
        <DialogTitle>Add New Branch</DialogTitle>
        <DialogContent>
          <CustomTextField
            autoFocus
            margin="dense"
            name="name"
            label="Branch Name"
            type="text"
            fullWidth
            variant="outlined"
            value={newBranchData.name}
            onChange={handleNewBranchChange}
            sx={{ mb: 2 }}
          />
          <CustomTextField
            margin="dense"
            name="address_one"
            label="Address One"
            type="text"
            fullWidth
            variant="outlined"
            value={newBranchData.address_one}
            onChange={handleNewBranchChange}
            sx={{ mb: 2 }}
          />
            <CustomTextField
            margin="dense"
            name="address_two"
            label="Address Two"
            type="text"
            fullWidth
            variant="outlined"
            value={newBranchData.address_two}
            onChange={handleNewBranchChange}
            sx={{ mb: 2 }}
          />
          
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseAddBranchDialog} color="secondary">
            Cancel
          </Button>
          <Button
            onClick={handleSaveNewBranch}
            color="primary"
            disabled={savingNewBranch}
          >
            {savingNewBranch ? <CircularProgress size={24} /> : "Save Branch"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Open Add Branch Rep Dialog */}
      <Dialog open={openAddBranchRepDialog} onClose={handleCloseAddBranchRepDialog}>
        <DialogTitle>Add New Branch Rep</DialogTitle>
        <DialogContent>
          <CustomTextField
            autoFocus
            margin="dense"
            name="name"
            label="Branch Rep Name"
            type="text"
            fullWidth
            variant="outlined"
            value={newBranchRepData.name}
            onChange={handleNewBranchRepChange}
            sx={{ mb: 2 }}
          />
          <CustomTextField
            margin="dense"
            name="email"
            label="Branch Rep Email Address"
            type="email"
            fullWidth
            variant="outlined"
            value={newBranchRepData.email}
            onChange={handleNewBranchRepChange}
            sx={{ mb: 2 }}
          />
          <PhoneNumberField
            margin="dense"
            name="phone"
            label="Phone Number"
            fullWidth
            variant="outlined"
            value={newBranchRepData.phone || ""}
            defaultCountry="ZW"
            onChange={handlePhoneBranchRepInputChange}
            sx={{ mb: 2 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseAddBranchRepDialog} color="secondary">
            Cancel
          </Button>
          <Button
            onClick={handleSaveNewBranchRep}
            color="primary"
            disabled={savingNewBranchRep}
          >
            {savingNewBranchRep ? <CircularProgress size={24} /> : "Save Branch Rep"}
          </Button>
        </DialogActions>
      </Dialog>
      
    </Card>
  );
};

export default AddCompanyContact;
